import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Calculator, Save, Download, FileText, Trash2, Eye, Sparkles,
} from "lucide-react";

const fmtINR = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  const num = Number(n);
  if (num === 0) return "₹0";
  return "₹" + num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

const SectionTile = ({ title, color = "slate", children, testId }) => {
  const colorMap = {
    slate: "border-slate-200 bg-slate-50/50",
    pink: "border-red-200 bg-red-50/40",
    emerald: "border-emerald-200 bg-emerald-50/40",
    amber: "border-amber-200 bg-amber-50/40",
    blue: "border-blue-200 bg-blue-50/40",
  };
  const headColor = {
    slate: "text-slate-700",
    pink: "text-red-700",
    emerald: "text-emerald-700",
    amber: "text-amber-800",
    blue: "text-blue-700",
  };
  return (
    <div className={`border rounded-lg p-3 ${colorMap[color]}`} data-testid={testId}>
      <div className={`text-[10px] uppercase tracking-widest font-bold mb-2 ${headColor[color]}`}>{title}</div>
      {children}
    </div>
  );
};

const Row = ({ label, value, bold = false, testId }) => (
  <div className={`flex justify-between text-xs py-1 ${bold ? "font-bold border-t border-current pt-1.5 mt-1" : ""}`} data-testid={testId}>
    <span className="text-slate-600">{label}</span>
    <span className="font-mono">{fmtINR(value)}</span>
  </div>
);

export default function SalaryCalculator() {
  const [inputMode, setInputMode] = useState("gross");
  const [value, setValue] = useState("35000");
  const [pfApplicable, setPfApplicable] = useState(true);
  const [esiOverride, setEsiOverride] = useState("auto"); // auto | yes | no
  const [bondApplicable, setBondApplicable] = useState(false);
  const [bondPct, setBondPct] = useState("10");
  // Phase G+ Rev 2026-05 — Rajasthan revised structure
  const [bonusApplicable, setBonusApplicable] = useState(true);
  const [daPct, setDaPct] = useState("0");
  const [rajMinWage, setRajMinWage] = useState("7438");

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveForm, setSaveForm] = useState({
    scenario_name: "",
    employee_name: "",
    designation: "",
    department: "",
    date_of_joining: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const buildPayload = () => ({
    input_mode: inputMode,
    value: parseFloat(value) || 0,
    pf_applicable: pfApplicable,
    esi_applicable_override: esiOverride === "auto" ? null : esiOverride === "yes",
    bond_applicable: bondApplicable,
    bond_pct: (parseFloat(bondPct) || 0) / 100,
    bonus_applicable: bonusApplicable,
    da_pct: (parseFloat(daPct) || 0) / 100,
    rajasthan_min_wage: parseFloat(rajMinWage) || 7438,
  });

  const compute = async () => {
    if (!value || parseFloat(value) <= 0) {
      toast.error("Enter a positive value");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/salary-calculator/compute", buildPayload());
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Compute failed");
    } finally {
      setLoading(false);
    }
  };

  const loadScenarios = async () => {
    try {
      const { data } = await api.get("/salary-calculator/scenarios");
      setScenarios(data || []);
    } catch (e) {
      console.error("[SalaryCalculator] loadScenarios failed", e);
    }
  };

  useEffect(() => { compute(); loadScenarios(); /* eslint-disable-next-line */ }, []);
  // Live recompute on key changes
  useEffect(() => {
    if (!value) return;
    const t = setTimeout(() => compute(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [inputMode, value, pfApplicable, esiOverride, bondApplicable, bondPct, bonusApplicable, daPct, rajMinWage]);

  const saveScenario = async () => {
    if (!saveForm.scenario_name || saveForm.scenario_name.length < 2) {
      toast.error("Scenario name is required (min 2 chars)");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...buildPayload(), ...saveForm };
      const { data } = await api.post("/salary-calculator/scenarios", payload);
      toast.success(`Scenario "${data.scenario_name}" saved`);
      setShowSaveDialog(false);
      setSaveForm({ scenario_name: "", employee_name: "", designation: "", department: "", date_of_joining: "", notes: "" });
      loadScenarios();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async (scenarioId) => {
    try {
      const resp = await api.get(`/salary-calculator/scenarios/${scenarioId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `SalaryStructure.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF download failed");
    }
  };

  const previewPdf = async () => {
    const name = saveForm.scenario_name || (saveForm.employee_name ? `${saveForm.employee_name} — Preview` : "Preview");
    try {
      const payload = { ...buildPayload(), ...saveForm, scenario_name: name };
      const resp = await api.post(`/salary-calculator/preview-pdf`, payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const deleteScenario = async (id, name) => {
    if (!window.confirm(`Delete scenario "${name}"?`)) return;
    try {
      await api.delete(`/salary-calculator/scenarios/${id}`);
      toast.success("Deleted");
      loadScenarios();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const inputLabel = useMemo(() => {
    if (inputMode === "gross") return "Gross Salary (Monthly)";
    if (inputMode === "inhand") return "Target In-Hand Salary (Monthly)";
    return "Target Monthly CTC";
  }, [inputMode]);

  return (
    <div className="space-y-4 max-w-7xl" data-testid="salary-calculator">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Calculator className="h-3 w-3" /> HR Tools
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Salary Calculator
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            For new joining offers. Enter Gross / In-Hand / CTC target → complete structure auto-computes → save & download printable PDF for signature.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* LEFT — Input Panel */}
        <div className="lg:col-span-1 space-y-3">
          <SectionTile title="Input" color="blue" testId="input-panel">
            <Label className="text-[10px] uppercase tracking-wider text-slate-600">Calculation Mode</Label>
            <Select value={inputMode} onValueChange={setInputMode}>
              <SelectTrigger className="mt-1 h-9 text-sm" data-testid="mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gross">Start from Gross Salary</SelectItem>
                <SelectItem value="inhand">Start from In-Hand (Reverse)</SelectItem>
                <SelectItem value="ctc">Start from CTC Target</SelectItem>
              </SelectContent>
            </Select>

            <Label className="text-[10px] uppercase tracking-wider text-slate-600 mt-3 block">{inputLabel}</Label>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-slate-500 font-mono">₹</span>
              <Input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="h-9 text-base"
                placeholder="35000"
                data-testid="value-input"
              />
            </div>

            <div className="mt-3 space-y-2.5 pt-3 border-t border-blue-200">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-700">PF Applicable</Label>
                <Switch checked={pfApplicable} onCheckedChange={setPfApplicable} data-testid="pf-toggle" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-600 block mb-1">ESI</Label>
                <Select value={esiOverride} onValueChange={setEsiOverride}>
                  <SelectTrigger className="h-8 text-xs" data-testid="esi-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (Yes if Basic+DA ≤ ₹21k)</SelectItem>
                    <SelectItem value="yes">Force Yes</SelectItem>
                    <SelectItem value="no">Force No</SelectItem>
                  </SelectContent>
                </Select>
                {esiOverride === "yes" && result?.earnings?.gross_salary > 21000 && (
                  <div
                    className="mt-1.5 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5 leading-snug"
                    data-testid="esi-force-yes-warning"
                  >
                    ⚠ Gross is above ₹21,000 — real payroll onboarding auto-sets
                    ESI to "No" at this gross for a new joiner. "Force Yes" only
                    reflects reality for an existing/grandfathered employee whose
                    ESI was already active before crossing the ceiling.
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-700">Statutory Bonus</Label>
                <Switch checked={bonusApplicable} onCheckedChange={setBonusApplicable} data-testid="bonus-toggle" />
              </div>
              {bonusApplicable && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-600 block mb-1">Rajasthan Min Wage (₹/mo)</Label>
                  <Input
                    type="number"
                    step="1"
                    value={rajMinWage}
                    onChange={(e) => setRajMinWage(e.target.value)}
                    className="h-8 text-xs"
                    placeholder="7438"
                    data-testid="raj-min-wage-input"
                  />
                  <div className="text-[10px] text-slate-500 mt-1">Bonus = 8.33% × max(₹7,000, this value)</div>
                </div>
              )}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-600 block mb-1">DA % (Dearness Allowance)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={daPct}
                  onChange={(e) => setDaPct(e.target.value)}
                  className="h-8 text-xs"
                  placeholder="0"
                  data-testid="da-pct-input"
                />
                <div className="text-[10px] text-slate-500 mt-1">Nil for most private firms — leave 0</div>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-700">Bond Applicable</Label>
                <Switch checked={bondApplicable} onCheckedChange={setBondApplicable} data-testid="bond-toggle" />
              </div>
              {bondApplicable && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-600 block mb-1">Bond %</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={bondPct}
                    onChange={(e) => setBondPct(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="bond-pct-input"
                  />
                </div>
              )}
            </div>

            <div className="mt-4 pt-3 border-t border-blue-200 flex gap-2">
              <Button
                onClick={() => setShowSaveDialog(true)}
                className="brand-bg brand-bg-hover text-white flex-1"
                size="sm"
                disabled={!result}
                data-testid="open-save-btn"
              >
                <Save className="h-3.5 w-3.5 mr-1.5" /> Save & PDF
              </Button>
            </div>
          </SectionTile>

          {/* Saved scenarios */}
          <SectionTile title={`Saved Scenarios (${scenarios.length})`} color="slate" testId="saved-panel">
            {scenarios.length === 0 ? (
              <div className="text-xs text-slate-500 italic py-2">No saved scenarios yet.</div>
            ) : (
              <div className="space-y-1.5 max-h-96 overflow-y-auto">
                {scenarios.map((s) => (
                  <div key={s.id} className="bg-white border border-slate-200 rounded p-2 flex items-center gap-2" data-testid={`scenario-${s.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-xs truncate">{s.scenario_name}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {s.employee_name && `${s.employee_name} · `}
                        Gross {fmtINR(s.earnings?.gross_salary)} · In-Hand {fmtINR(s.net?.in_hand_salary)}
                      </div>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => downloadPdf(s.id)} title="Download PDF">
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteScenario(s.id, s.scenario_name)} title="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </SectionTile>
        </div>

        {/* RIGHT — Result Panel */}
        <div className="lg:col-span-2">
          {!result ? (
            <div className="bg-white border-2 border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400">
              <Calculator className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{loading ? "Computing…" : "Enter a value to see the structure"}</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="result-panel">
              {/* Big summary */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5 text-center">
                  <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-semibold">Gross / Month</div>
                  <div className="text-xl font-bold text-emerald-700 mt-1" style={{ fontFamily: "Chivo" }} data-testid="big-gross">
                    {fmtINR(result.earnings?.gross_salary)}
                  </div>
                </div>
                <div className="bg-red-50 border border-red-300 rounded p-2.5 text-center">
                  <div className="text-[9px] uppercase tracking-widest text-red-700 font-semibold">In-Hand / Month</div>
                  <div className="text-xl font-bold text-red-700 mt-1" style={{ fontFamily: "Chivo" }} data-testid="big-inhand">
                    {fmtINR(result.net?.in_hand_salary)}
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-center">
                  <div className="text-[9px] uppercase tracking-widest text-amber-800 font-semibold">Monthly CTC</div>
                  <div className="text-xl font-bold text-amber-800 mt-1" style={{ fontFamily: "Chivo" }} data-testid="big-ctc">
                    {fmtINR(result.ctc?.monthly_ctc)}
                  </div>
                </div>
              </div>

              <div className="text-[10px] text-slate-500 text-center">
                Annual CTC: <b className="text-slate-700">{fmtINR(result.ctc?.annual_ctc)}</b>
                {inputMode !== "gross" && (
                  <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">
                    <Sparkles className="h-2.5 w-2.5" /> Reverse-solved from {inputMode === "inhand" ? "In-Hand" : "CTC"} ₹{Number(value).toLocaleString("en-IN")}
                  </span>
                )}
              </div>

              {/* Breakdown grid */}
              <div className="grid md:grid-cols-3 gap-3 pt-2 border-t border-slate-200">
                <SectionTile title="1. Earnings" color="emerald">
                  <Row label="Basic (50%)" value={result.earnings?.basic} testId="row-basic" />
                  {result.earnings?.statutory_bonus > 0 ? (
                    <Row label={`HRA (30% − Bonus offset)`} value={result.earnings?.hra} testId="row-hra" />
                  ) : (
                    <Row label="HRA (30%)" value={result.earnings?.hra} testId="row-hra" />
                  )}
                  <Row label="Conveyance (20%)" value={result.earnings?.conveyance} testId="row-conv" />
                  {result.earnings?.da > 0 && (
                    <Row label={`DA (${Math.round((result.inputs?.da_pct || 0) * 100)}%)`} value={result.earnings?.da} testId="row-da" />
                  )}
                  {result.earnings?.statutory_bonus > 0 && (
                    <Row label="Statutory Bonus (8.33%)" value={result.earnings?.statutory_bonus} testId="row-bonus" />
                  )}
                  <Row label="Total Earnings (= Gross)" value={result.earnings?.total_monthly_earnings} bold testId="row-total-earnings" />
                </SectionTile>

                <SectionTile title="2. Employee Deductions" color="amber">
                  <Row label="PF (12% Basic)" value={result.employee_deductions?.pf_employee} />
                  <Row label="ESI (0.75% Gross)" value={result.employee_deductions?.esi_employee} />
                  {/* Professional Tax — NOT applicable in Rajasthan, row hidden */}
                  {result.employee_deductions?.loss_of_pay > 0 && (
                    <Row label={`LOP (${result.inputs?.lop_days || 0} day${(result.inputs?.lop_days || 0) === 1 ? '' : 's'})`} value={result.employee_deductions?.loss_of_pay} />
                  )}
                  {result.employee_deductions?.bond_deduction > 0 && (
                    <Row label="Bond Deduction" value={result.employee_deductions?.bond_deduction} />
                  )}
                  <Row label="Total Deductions" value={result.employee_deductions?.total_deductions} bold />
                </SectionTile>

                <SectionTile title="3. Employer Contributions" color="blue">
                  <Row label="EPF (3.67%)" value={result.employer_contributions?.epf_employer} />
                  <Row label={`EPS (8.33%, max ${fmtINR(result.employer_contributions?.eps_cap_used || 1250)})`} value={result.employer_contributions?.eps_employer} />
                  <Row label="EDLI (0.5%)" value={result.employer_contributions?.edli} />
                  <Row label="PF Admin (0.5%)" value={result.employer_contributions?.pf_admin} />
                  <Row label="Employer ESI (3.25%)" value={result.employer_contributions?.esi_employer} />
                  <Row label="Gratuity (4.81%)" value={result.employer_contributions?.gratuity} />
                </SectionTile>
              </div>

              {/* Validation warnings */}
              {result.validations?.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-[11px] text-amber-900" data-testid="validation-warnings">
                  <div className="font-semibold mb-1 uppercase tracking-wider text-[9px]">Compliance Warnings</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {result.validations.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {/* Net flow */}
              <div className="bg-slate-50 border border-slate-200 rounded p-3 mt-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold mb-2">Net Flow</div>
                <Row label="Total Earnings (= Gross)" value={result.earnings?.total_monthly_earnings} bold />
                <Row label="− Employee PF" value={-result.employee_deductions?.pf_employee} />
                <Row label="− Employee ESI" value={-result.employee_deductions?.esi_employee} />
                {result.employee_deductions?.loss_of_pay > 0 && (
                  <Row label="− Loss of Pay" value={-result.employee_deductions?.loss_of_pay} />
                )}
                <Row label="= Net Salary (before bond)" value={result.net?.net_salary_before_bond} bold />
                {result.employee_deductions?.bond_deduction > 0 && (
                  <Row label="− Bond Deduction (refundable)" value={-result.employee_deductions?.bond_deduction} />
                )}
                <Row label="= IN-HAND SALARY" value={result.net?.in_hand_salary} bold />
              </div>

              <div className="text-[10px] text-slate-500 pt-1 border-t border-slate-100">
                <b>Notes:</b> Statutory Bonus is offset from HRA so Total Earnings = Gross (no change in employee&apos;s headline number). Professional Tax NOT levied in Rajasthan. ESI applies when (Basic+DA) ≤ ₹21,000 per ESIC Dec 2025. Bonus = 8.33% × max(₹7,000, Rajasthan Min Wage); eligible when (Basic+DA) ≤ ₹21,000. PF capped at Basic {fmtINR(result.employer_contributions?.pf_wage_cap_used || 15000)}.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="max-w-lg" data-testid="save-dialog">
          <DialogHeader>
            <DialogTitle>Save Salary Scenario</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Scenario Name *</Label>
              <Input
                value={saveForm.scenario_name}
                onChange={(e) => setSaveForm({ ...saveForm, scenario_name: e.target.value })}
                placeholder="e.g., Ramesh Kumar - Offer 2026"
                className="mt-1"
                data-testid="save-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Employee Name</Label>
                <Input
                  value={saveForm.employee_name}
                  onChange={(e) => setSaveForm({ ...saveForm, employee_name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Designation</Label>
                <Input
                  value={saveForm.designation}
                  onChange={(e) => setSaveForm({ ...saveForm, designation: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Department</Label>
                <Input
                  value={saveForm.department}
                  onChange={(e) => setSaveForm({ ...saveForm, department: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">DOJ</Label>
                <Input
                  type="date"
                  value={saveForm.date_of_joining}
                  onChange={(e) => setSaveForm({ ...saveForm, date_of_joining: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Notes</Label>
              <Textarea
                value={saveForm.notes}
                onChange={(e) => setSaveForm({ ...saveForm, notes: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={previewPdf} data-testid="preview-pdf-btn">
              <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview PDF
            </Button>
            <Button onClick={saveScenario} disabled={saving} className="brand-bg brand-bg-hover text-white" data-testid="save-confirm-btn">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Saving…" : "Save Scenario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
